import { DatePicker } from "antd";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import { clsx } from "clsx";
import type { Dayjs } from "dayjs";
import { ComponentProps, forwardRef, useState } from "react";
import { hasActiveTextSelection } from "~/utils/textInputUtils";
import { useDateRangePicker } from "../../hooks/useDateRangePicker";
import { DateISO } from "../../utils/DateISO";
import { Tooltip } from "../Tooltip";
import "./DateRangePicker.css";

const { RangePicker } = DatePicker;

export interface DateRangePickerProps {
  variant?: "outline" | "filled";
  startDate?: DateISO | null;
  endDate?: DateISO | null;
  onChange?: (startDate: DateISO | null, endDate: DateISO | null) => void;
  disabled?: boolean;
  isError?: boolean;
  clearError?: () => void;
  className?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  label?: string;
  dataTestId?: string;
  datePopupTestId?: string;
  pickerProps?: Omit<
    ComponentProps<typeof RangePicker>,
    "value" | "onChange" | "placeholder" | "disabled" | "size"
  >;
  /**
   * Size of the control
   * - "small": 24px height
   * - "default": 32px height
   * - "large": 40px height
   * @default "default"
   */
  size?: "small" | "default" | "large";
  /**
   * For filters: either date may be left blank. A blank start means "from any
   * time in the past", a blank end "to any time in the future" — the caller
   * gets `null` for that side and leaves it unbounded. Off for form fields,
   * where a range is only complete with both dates.
   *
   * antd can't clear one side on its own: deleting one date's text restores
   * it. Going from a full range to a one-sided one takes × and a re-pick.
   * @default false
   */
  openEnded?: boolean;
}

/** The read-only text: a one-sided open-ended range reads as such, not "-". */
const describeRange = (start: string, end: string, openEnded: boolean) => {
  if (start && end) return `${start} - ${end}`;
  if (openEnded && start) return `From ${start}`;
  if (openEnded && end) return `Until ${end}`;
  return "-";
};

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      startDate,
      endDate,
      onChange,
      disabled = false,
      isError = false,
      clearError,
      className = "",
      startPlaceholder = "Start date",
      endPlaceholder = "End date",
      label,
      dataTestId,
      datePopupTestId,
      pickerProps = {},
      variant = "filled",
      size = "default",
      openEnded = false,
    },
    ref,
  ) => {
    const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
    const { dateRange, setDateRange, formattedStartDate, formattedEndDate } =
      useDateRangePicker({
        initialStartDate: startDate,
        initialEndDate: endDate,
        onChange,
      });

    // An open-ended range counts (and shows) with either side set; a closed
    // one only once both are.
    const isDateRangeSelected = openEnded
      ? Boolean(dateRange.start || dateRange.end)
      : Boolean(dateRange.start && dateRange.end);
    const rangeValue: [Dayjs | null, Dayjs | null] | null = isDateRangeSelected
      ? [dateRange.start, dateRange.end]
      : null;
    const antSize: SizeType =
      size === "small" ? "small" : size === "large" ? "large" : "middle";

    if (disabled) {
      const displayValue = describeRange(
        formattedStartDate,
        formattedEndDate,
        openEnded,
      );

      const handleCopyOnClick = () => {
        if (hasActiveTextSelection()) {
          return;
        }

        if (displayValue !== "-") {
          navigator.clipboard
            .writeText(displayValue)
            .then(() => {
              setShowCopiedTooltip(true);
              setTimeout(() => setShowCopiedTooltip(false), 2000);
            })
            .catch(() => {
              // Silently fail if clipboard access is denied
            });
        }
      };

      return (
        <div
          className={`indigo-date-range-static ${className}`}
          ref={ref}
          data-testid={dataTestId}
        >
          {label && <div className="indigo-date-range-label">{label}</div>}
          <Tooltip
            content="Copied!"
            placement="top"
            size="sm"
            visible={showCopiedTooltip}
          >
            <div
              className="indigo-date-range-value indigo-text-disabled"
              onClick={handleCopyOnClick}
            >
              {displayValue}
            </div>
          </Tooltip>
        </div>
      );
    }

    const handleDateRangeChange: ComponentProps<
      typeof RangePicker
    >["onChange"] = (values, formatStrings) => {
      if (values) {
        // Call setDateRange even if only one date is present
        setDateRange(values, formatStrings);
      } else {
        setDateRange(null, formatStrings);
      }
    };

    return (
      <div
        className={clsx(
          "indigo-date-range",
          // variant styles
          {
            "indigo-date-range--filled": variant === "filled",
          },
          `indigo-date-range--${size}`,
          className,
        )}
        ref={ref}
      >
        {label && <div className="indigo-date-range-label">{label}</div>}
        <RangePicker
          size={antSize}
          value={rangeValue}
          onChange={handleDateRangeChange}
          onFocus={clearError}
          disabled={disabled}
          className={clsx(
            "indigo-date-range-picker",
            `indigo-date-range-picker--${size}`,
            isDateRangeSelected ? "has-value" : "no-value",
            className,
          )}
          placeholder={[startPlaceholder, endPlaceholder]}
          format={["MM/DD/YYYY", "M/D/YYYY"]}
          allowEmpty={openEnded ? [true, true] : undefined}
          status={isError ? "error" : ""}
          data-testid={dataTestId}
          classNames={{
            popup: {
              root: `indigo-date-range-picker-dropdown ${datePopupTestId || ""}`,
            },
          }}
          {...pickerProps}
        />
      </div>
    );
  },
);

DateRangePicker.displayName = "DateRangePicker";

export default DateRangePicker;
